import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import Header from "../Common/Header";
import { io } from "socket.io-client";
import Sidebar from "../Common/Sidebar";
import Footer from "../Common/Footer";
import { GMCContext } from "../../GMCContext";
import Swal from "sweetalert2";
import Copy from "../Common/Copy";
import axios from "axios";
import Pagination from "../Common/Pagination";
import { utils } from "ethers";
import { getApiBaseUrl, getServerBaseUrl } from "../../utils/serverUrl";

let socket = null;
const getSocket = () => {
  if (!socket) {
    socket = io(getServerBaseUrl(), {
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
    });
    socket.on("connect_error", (err) => {
      console.warn("Socket connect failed, using HTTP polling:", err.message);
    });
  }
  return socket;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const buildSelectedItemsHtml = (selectedIds, rows) => {
  const items = rows.filter((item) => selectedIds.includes(item._id));
  if (items.length === 0) {
    return `<p style="text-align:center;margin:0;">No selected addresses found.</p>`;
  }

  const list = items
    .map(
      (item, i) => `
      <div style="text-align:left;padding:8px 10px;margin-bottom:6px;background:#f8f9fa;border-radius:8px;border:1px solid #e9ecef;">
        <p style="margin:0 0 4px;"><b>Username:</b> ${escapeHtml(item.username)}</p>
        <p style="margin:0;word-break:break-all;font-size:13px;"><b>Address:</b> ${escapeHtml(item.address)}</p>
      </div>
    `
    )
    .join("");

  return `
    <p style="text-align:center;margin:0 0 10px;">Confirm approval for <b>${items.length}</b> address(es):</p>
    <div style="max-height:200px;overflow-y:auto;padding-right:4px;">${list}</div>
  `;
};

const buildAuthOtpHtml = (detailsHtml) => `
  ${detailsHtml}
  <form autocomplete="off" onsubmit="return false;" style="margin-top:16px;">
    <p style="margin:0 0 10px;font-weight:600;color:#495057;text-align:left;">Authentication Key</p>
    <div class="auth-otp-row" style="display:flex;justify-content:center;gap:10px;">
      ${[0, 1, 2, 3, 4, 5]
    .map(
      (i) => `
        <input
          type="text"
          maxlength="1"
          class="auth-otp-digit"
          data-index="${i}"
          inputmode="numeric"
          pattern="[0-9]*"
          autocomplete="one-time-code"
          aria-label="Digit ${i + 1}"
          style="width:44px;height:50px;text-align:center;font-size:22px;font-weight:600;border:2px solid #ced4da;border-radius:10px;outline:none;box-sizing:border-box;"
        />
      `
    )
    .join("")}
    </div>
    <p style="font-size:12px;color:#6c757d;margin:10px 0 0;text-align:center;">Enter 6-digit code only</p>
  </form>
`;

const setupAuthOtpInputs = () => {
  const inputs = document.querySelectorAll(".auth-otp-digit");
  const focusStyle = (el, active) => {
    el.style.borderColor = active ? "#0d6efd" : "#ced4da";
    el.style.boxShadow = active ? "0 0 0 3px rgba(13,110,253,.15)" : "none";
  };

  inputs.forEach((input, idx) => {
    input.addEventListener("focus", () => focusStyle(input, true));
    input.addEventListener("blur", () => focusStyle(input, false));

    input.addEventListener("input", (e) => {
      const digit = e.target.value.replace(/\D/g, "").slice(-1);
      e.target.value = digit;
      if (digit && idx < inputs.length - 1) {
        inputs[idx + 1].focus();
      }
    });

    input.addEventListener("keydown", (e) => {
      if (e.key === "Backspace" && !e.target.value && idx > 0) {
        inputs[idx - 1].focus();
        inputs[idx - 1].value = "";
      }
      if (e.key === "ArrowLeft" && idx > 0) inputs[idx - 1].focus();
      if (e.key === "ArrowRight" && idx < inputs.length - 1) inputs[idx + 1].focus();
    });

    input.addEventListener("paste", (e) => {
      e.preventDefault();
      const pasted = (e.clipboardData.getData("text") || "")
        .replace(/\D/g, "")
        .slice(0, 6);
      pasted.split("").forEach((ch, i) => {
        if (inputs[i]) inputs[i].value = ch;
      });
      const next = Math.min(pasted.length, inputs.length - 1);
      inputs[next]?.focus();
    });
  });

  inputs[0]?.focus();
};

const getAuthOtpValue = () =>
  Array.from(document.querySelectorAll(".auth-otp-digit"))
    .map((el) => el.value)
    .join("");

const validateAuthOtp = () => {
  const code = getAuthOtpValue();
  if (!/^\d{6}$/.test(code)) {
    Swal.showValidationMessage("Please enter a valid 6-digit authentication key");
    return false;
  }
  return code;
};

export default function WithdrawalAddressApproval() {
  const { setIsSidebarEnabled, formatDateTime } = useContext(GMCContext);
  const statedata = JSON.parse(localStorage.getItem('mark#0143'))
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pages, setPages] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [refreshKey, setRefreshKey] = useState(0);
  const [progress, setProgress] = useState({
    total: 0,
    completed: 0,
    status: "idle",
  });
  const [jobId, setJobId] = useState(null);
  const [showBlocker, setShowBlocker] = useState(false);
  const [blockerStatus, setBlockerStatus] = useState("loading");
  const [rejectingId, setRejectingId] = useState(null);
  const jobIdRef = useRef(null);

  const isPendingTab = activeTab === "pending";
  const isApprovedTab = activeTab === "approved";
  const isRejectedTab = activeTab === "rejected";
  const isProcessing = showBlocker && blockerStatus === "loading";

  const getListSubmethod = () => {
    if (isPendingTab) return "getallpendingaddress";
    if (isApprovedTab) return "getallapproveaddress";
    return "getallrejectaddress";
  };

  const tableColSpan = isPendingTab ? 7 : 4;
  const dateColumnLabel = isPendingTab
    ? "Date"
    : isApprovedTab
      ? "Approved Date"
      : "Rejected Date";
  const emptyTableMessage = isPendingTab
    ? "No pending addresses"
    : isApprovedTab
      ? "No approved addresses"
      : "No rejected addresses";

  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  const applyProgressUpdate = useCallback((data) => {
    if (!data?.status || data.status === "not_found") return;

    setProgress((prev) => ({
      ...prev,
      total: data.total ?? prev.total,
      completed: data.completed ?? prev.completed,
      status: data.status,
      phase: data.phase,
    }));

    if (data.status === "processing") {
      setShowBlocker(true);
      setBlockerStatus("loading");
    }

    if (data.status === "done") {
      setBlockerStatus("success");
      setSelected([]);
      setRefreshKey((k) => k + 1);
    }

    if (data.status === "error") {
      setBlockerStatus("error");
    }
  }, []);

  const resetProgressBlocker = () => {
    setShowBlocker(false);
    setBlockerStatus("loading");
    setJobId(null);
    jobIdRef.current = null;
    setProgress({ total: 0, completed: 0, status: "idle", phase: null });
  };

  const handleApprove = (item) => {
    Swal.fire({
      title: "Approve Address?",
      html: buildAuthOtpHtml(`
        <p style="text-align:left;margin:0 0 6px;"><b>Username:</b> ${item.username}</p>
        <p style="text-align:left;margin:0 0 6px;"><b>Address:</b> ${item.address}</p>
        <p style="color:#0d6efd;text-align:left;margin:0;">This withdrawal address will be approved.</p>
      `),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel",
      didOpen: setupAuthOtpInputs,
      preConfirm: validateAuthOtp,
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        try {
          const res = await axios.post(process.env.REACT_APP_BACKEND_LINK, {
            method: "admin",
            submethod: "approveaddress",
            key: process.env.REACT_APP_KEY,
            username: item.username,
            _id: statedata?.id,
            authNumber: result.value,
            // address: item.address,
          });
          if (res.data?.resCode === 200) {
            Swal.fire("Approved!", res.data.msg || "Address approved successfully.", "success");
            triggerRefresh();
          } else {
            Swal.fire("Error", res.data?.error || "Failed to approve.", "error");
          }
        } catch (err) {
          Swal.fire("Error", "Server error.", "error");
        }
      }
    });
  };

  const handleReject = (item) => {
    Swal.fire({
      title: "Reject Address?",
      html: buildAuthOtpHtml(`
        <p style="text-align:left;margin:0 0 6px;"><b>Username:</b> ${escapeHtml(item.username)}</p>
        <p style="text-align:left;margin:0 0 6px;"><b>Address:</b> ${escapeHtml(item.address)}</p>
        <p style="color:#dc3545;text-align:left;margin:0;">This withdrawal address will be rejected.</p>
      `),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Reject",
      cancelButtonText: "Cancel",
      didOpen: setupAuthOtpInputs,
      preConfirm: validateAuthOtp,
    }).then(async (result) => {
      if (!result.isConfirmed || !result.value) return;

      setRejectingId(item._id);
      try {
        const res = await axios.post(process.env.REACT_APP_BACKEND_LINK, {
          method: "admin",
          submethod: "rejectaddress",
          key: process.env.REACT_APP_KEY,
          username: item.username,
          authNumber: result.value,
          _id: statedata?.id,
        });

        if (res.data?.resCode === 200) {
          Swal.fire(
            "Rejected!",
            res.data.msg || "Reject Address Successfully",
            "success"
          );
          setSelected((prev) => prev.filter((id) => id !== item._id));
          triggerRefresh();
        } else {
          Swal.fire("Error", res.data?.error || "Failed to reject address.", "error");
        }
      } catch (err) {
        Swal.fire("Error", "Server error.", "error");
      } finally {
        setRejectingId(null);
      }
    });
  };

  const handleApproveSelected = () => {
    if (selected.length === 0) return;
    Swal.fire({
      title: `Approve ${selected.length} address(es)?`,
      html: buildAuthOtpHtml(buildSelectedItemsHtml(selected, data)),
      width: 520,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Approve All",
      cancelButtonText: "Cancel",
      didOpen: setupAuthOtpInputs,
      preConfirm: validateAuthOtp,
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        const selectedRows = data.filter((item) => selected.includes(item._id));
        const selectedUsernames = selectedRows.map((item) => item.username);

        const jobHash = utils.id(Math.random().toString());
        jobIdRef.current = jobHash;
        setJobId(jobHash);
        setShowBlocker(true);
        setBlockerStatus("loading");
        setProgress({
          total: selectedUsernames.length,
          completed: 0,
          status: "processing",
        });

        const pollOnce = async () => {
          try {
            const pollRes = await axios.get(
              `${getApiBaseUrl()}/api/addressapprove-progress/${encodeURIComponent(jobHash)}`
            );
            applyProgressUpdate(pollRes.data);
          } catch (_) {
            /* wait for backend */
          }
        };

        const pollInterval = setInterval(pollOnce, 400);

        try {
          const res = await axios.post(process.env.REACT_APP_BACKEND_LINK, {
            method: "admin",
            submethod: "approveall",
            key: process.env.REACT_APP_KEY,
            ids: selected,
            username: selectedUsernames,
            authNumber: result.value,
            _id: statedata?.id,
            hash: jobHash,
          });
          if (res.data?.resCode !== 200) {
            resetProgressBlocker();
            Swal.fire("Error", res.data?.error || "Failed.", "error");
          } else {
            await pollOnce();
          }
        } catch (err) {
          resetProgressBlocker();
          Swal.fire("Error", "Server error.", "error");
        } finally {
          clearInterval(pollInterval);
        }
      }
    });
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(data.map((item) => item._id));
    } else {
      setSelected([]);
    }
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setData([]);
    setPages(1);
    setLoading(true);
    setActiveTab(tab);
    setCurrentPage(1);
    setSelected([]);
    setSearch("");
  };

  useEffect(() => {
    let isActive = true;

    const fetchList = async () => {
      setLoading(true);
      setData([]);

      try {
        const res = await axios.post(process.env.REACT_APP_BACKEND_LINK, {
          method: "admin",
          submethod: getListSubmethod(),
          key: process.env.REACT_APP_KEY,
          search,
          page: currentPage,
          pageSize,
        });

        if (!isActive) return;

        if (res.data?.resCode === 200 && Array.isArray(res.data?.data)) {
          setData(res.data.data);
          setPages(Math.ceil((res.data?.total || 0) / pageSize));
        } else {
          setData([]);
          setPages(1);
        }
      } catch (err) {
        if (!isActive) return;
        console.error("Error fetching data:", err);
        setData([]);
        setPages(1);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchList();

    return () => {
      isActive = false;
    };
  }, [activeTab, currentPage, pageSize, search, refreshKey]);

  useEffect(() => {
    const onProgress = (data) => {
      const currentJobId = jobIdRef.current;
      const match =
        currentJobId &&
        data.jobId &&
        data.jobId.toLowerCase() === currentJobId.toLowerCase();

      if (!currentJobId || match) {
        applyProgressUpdate(data);
      }
    };

    const activeSocket = getSocket();
    activeSocket.on("addressapprove-progress", onProgress);
    return () => activeSocket.off("addressapprove-progress", onProgress);
  }, [applyProgressUpdate]);

  useEffect(() => {
    if (!jobId || blockerStatus !== "loading") return undefined;

    const pollProgress = async () => {
      try {
        const res = await axios.get(
          `${getApiBaseUrl()}/api/addressapprove-progress/${encodeURIComponent(jobId)}`
        );
        applyProgressUpdate(res.data);
      } catch (err) {
        console.warn("Progress poll failed:", err.message);
      }
    };

    pollProgress();
    const intervalId = setInterval(pollProgress, 400);
    return () => clearInterval(intervalId);
  }, [jobId, blockerStatus, applyProgressUpdate]);

  return (
    <>
      {showBlocker && (
        <div className="global-operation-blocker">
          <div className="blocker-content">
            {blockerStatus === "loading" ? (
              <>
                <div className="spinning-ring" />
                <i className="ri-error-warning-line forbidden-icon" />
                <p className="fw-bold">
                  {progress.phase === "ipfs"
                    ? `Uploading approved list (${progress.completed} / ${progress.total} done)`
                    : progress.total > 0
                      ? `Approving addresses: ${progress.completed} of ${progress.total} completed`
                      : "Starting approval..."}
                </p>
                {progress.total > 0 && (
                  <div className="w-100 mt-3" style={{ minWidth: "300px" }}>
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-muted">Progress</small>
                      <small className="fw-semibold text-primary">
                        {progress.completed} / {progress.total} (
                        {Math.round(
                          (progress.completed / progress.total) * 100
                        )}
                        %)
                      </small>
                    </div>
                    <div className="progress" style={{ height: "12px" }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                        role="progressbar"
                        style={{
                          width: `${progress.total ? (progress.completed / progress.total) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
                <small className="text-muted">
                  Page interaction is restricted during processing
                </small>
              </>
            ) : blockerStatus === "success" ? (
              <>
                <i className="ri-checkbox-circle-fill text-success fs-1" />
                <p className="text-success fw-bold mb-0">Addresses Approved!</p>
                <button
                  className="btn btn-success mt-3 px-5 fw-bold"
                  onClick={resetProgressBlocker}
                >
                  OK
                </button>
              </>
            ) : (
              <>
                <i className="ri-close-circle-fill text-danger fs-1" />
                <p className="text-danger fw-bold mb-0">Approval Failed</p>
                <button
                  className="btn btn-danger mt-3 px-5 fw-bold"
                  onClick={resetProgressBlocker}
                >
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div
        id="layout-wrapper"
        style={{
          opacity: isProcessing ? 0.5 : 1,
          pointerEvents: isProcessing ? "none" : "all",
          transition: "all 0.3s ease",
        }}
      >
        <Header />
        <Sidebar />
        <div
          className="vertical-overlay"
          onClick={() => setIsSidebarEnabled((prev) => !prev)}
        />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between bg-galaxy-transparent">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item">
                        <a href="javascript: void(0);">Withdrawal</a>
                      </li>
                      <li className="breadcrumb-item active">
                        Withdrawal Address Approval
                      </li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <h4 className="card-title mb-0">Withdrawal Address Approval</h4>
                        <div className="btn-group flex-wrap" role="group">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              isPendingTab ? "btn-primary" : "btn-outline-primary"
                            }`}
                            onClick={() => handleTabChange("pending")}
                          >
                            Pending
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              isApprovedTab ? "btn-success" : "btn-outline-success"
                            }`}
                            onClick={() => handleTabChange("approved")}
                          >
                            Approved
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              isRejectedTab ? "btn-danger" : "btn-outline-danger"
                            }`}
                            onClick={() => handleTabChange("rejected")}
                          >
                            Rejected
                          </button>
                        </div>
                      </div>
                      {isPendingTab && selected.length > 0 && (
                        <button
                          className="btn btn-success btn-sm px-3"
                          onClick={handleApproveSelected}
                          disabled={isProcessing}
                        >
                          Approve Selected ({selected.length})
                        </button>
                      )}
                    </div>
                    <div className="card-body">
                      <div className="listjs-table" id="addressApprovalList">
                        {/* Search & Filters */}
                        <div className="row g-4 mb-3">
                          {isPendingTab && (
                            <div className="col-sm-auto">
                              <div className="form-check d-flex align-items-center gap-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="selectAllChk"
                                  checked={
                                    data.length > 0 &&
                                    selected.length === data.length
                                  }
                                  onChange={toggleSelectAll}
                                />
                                <label
                                  className="form-check-label fw-medium mb-0"
                                  htmlFor="selectAllChk"
                                >
                                  Select All
                                </label>
                              </div>
                            </div>
                          )}
                          <div className="col-sm">
                            <div className="d-flex justify-content-sm-end">
                              <div className="search-box ms-2">
                                <input
                                  type="text"
                                  className="form-control search"
                                  placeholder="Search..."
                                  value={search}
                                  onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                  }}
                                />
                                <i className="ri-search-line search-icon" />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Table */}
                        <div className="table-responsive table-card mt-3 mb-1">
                          <table
                            className="table align-middle table-nowrap"
                            id="addressApprovalTable"
                          >
                            <thead className="table-light">
                              <tr>
                                {isPendingTab && (
                                  <th scope="col" style={{ width: "50px" }}>
                                    Select
                                  </th>
                                )}
                                <th className="gridjs-th text-center">#</th>
                                <th className="gridjs-th text-center">Username</th>
                                <th className="gridjs-th text-center">Address</th>
                                {isPendingTab && (
                                  <th className="gridjs-th text-center">Status</th>
                                )}
                                <th className="gridjs-th text-center">
                                  {dateColumnLabel}
                                </th>
                                {isPendingTab && (
                                  <th className="gridjs-th text-center" style={{ width: "88px" }}>
                                    Action
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr>
                                  <td
                                    colSpan={tableColSpan}
                                    className="text-center py-4"
                                  >
                                    <div
                                      className="spinner-border text-primary"
                                      role="status"
                                    >
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : data.length > 0 ? (
                                data.map((ele, i) => {
                                  const index =
                                    currentPage * pageSize - pageSize + (i + 1);
                                  return (
                                    <tr key={ele._id || `${activeTab}-${i}`}>
                                      {isPendingTab && (
                                        <td className="gridjs-td text-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              checked={selected.includes(ele._id)}
                                              onChange={() => toggleSelect(ele._id)}
                                            />
                                          </div>
                                        </td>
                                      )}
                                      <td className="gridjs-td text-center">
                                        {index}
                                      </td>
                                      <td className="gridjs-td text-center">
                                        <span className="fw-medium">
                                          {ele.username || "-"}
                                        </span>
                                        <Copy data={ele.username} />
                                      </td>
                                      <td className="gridjs-td text-center">
                                        <a
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-primary"
                                          href={`${process.env.REACT_APP_EXPLORER}/address/${ele.address}`}
                                        >
                                          {ele.address || "-"}
                                        </a>
                                        {ele.address && (
                                          <Copy data={ele.address} />
                                        )}
                                      </td>
                                      {isPendingTab && (
                                        <td className="gridjs-td text-center">
                                          <span
                                            className={`badge ${ele.status === 1 || ele.status === "approved"
                                                ? "bg-success"
                                                : ele.status === 2 || ele.status === "rejected"
                                                  ? "bg-danger"
                                                  : "bg-warning text-dark"
                                              }`}
                                          >
                                            {ele.status === 1 || ele.status === "approved"
                                              ? "Approved"
                                              : ele.status === 2 || ele.status === "rejected"
                                                ? "Rejected"
                                                : "Pending"}
                                          </span>
                                        </td>
                                      )}
                                      <td className="gridjs-td text-center">
                                        {formatDateTime(
                                          isPendingTab
                                            ? ele.createdAt
                                            : isApprovedTab
                                              ? ele.approvedAt ||
                                                ele.approveDate ||
                                                ele.adminApprovedate ||
                                                ele.updatedAt ||
                                                ele.createdAt
                                              : ele.rejectedAt ||
                                                ele.rejectDate ||
                                                ele.updatedAt ||
                                                ele.createdAt
                                        )}
                                      </td>
                                      {isPendingTab && (
                                        <td className="gridjs-td text-center">
                                          <div className="d-flex gap-1 justify-content-center align-items-center">
                                            <button
                                              type="button"
                                              className="btn btn-soft-success btn-sm btn-icon rounded-circle material-shadow-none"
                                              title="Approve"
                                              aria-label="Approve address"
                                              onClick={() => handleApprove(ele)}
                                              disabled={
                                                isProcessing ||
                                                rejectingId === ele._id ||
                                                ele.status === 1 ||
                                                ele.status === "approved" ||
                                                ele.status === 2 ||
                                                ele.status === "rejected"
                                              }
                                            >
                                              <i className="ri-check-line fs-16" />
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-soft-danger btn-sm btn-icon rounded-circle material-shadow-none"
                                              title="Reject"
                                              aria-label="Reject address"
                                              onClick={() => handleReject(ele)}
                                              disabled={
                                                isProcessing ||
                                                rejectingId === ele._id ||
                                                ele.status === 1 ||
                                                ele.status === "approved" ||
                                                ele.status === 2 ||
                                                ele.status === "rejected"
                                              }
                                            >
                                              {rejectingId === ele._id ? (
                                                <span
                                                  className="spinner-border spinner-border-sm"
                                                  role="status"
                                                  aria-hidden="true"
                                                />
                                              ) : (
                                                <i className="ri-close-line fs-16" />
                                              )}
                                            </button>
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td
                                    colSpan={tableColSpan}
                                    className="text-center py-4"
                                  >
                                    {emptyTableMessage}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        <Pagination
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          pages={pages}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      <style>
        {`
          .global-operation-blocker {
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.45);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: all;
          }

          .blocker-content {
            background: white;
            padding: 40px;
            border-radius: 30px;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 15px;
            border: 1px solid #f1f5f9;
            max-width: 90%;
            position: relative;
          }

          .forbidden-icon {
            font-size: 32px;
            color: #ef4444;
            position: absolute;
            top: 55px;
          }

          .spinning-ring {
            width: 80px;
            height: 80px;
            border: 4px solid #f1f5f9;
            border-top: 4px solid #405189;
            border-radius: 50%;
            animation: address-approve-spin 1s linear infinite;
          }

          @keyframes address-approve-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
}
